import {
  Alert,
  Collapse,
  Radio,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { useSearchResources } from '@medplum/react-hooks';
import { ORG_TYPE_PAYER } from '@unify/fhir-model/constants';
import { isPayTypeIntakeDisabled } from '../intakeTemporaryRestrictions';
import type { IntakeAction, IntakeAnswers, PayType, SubscriberRelationship } from '../types';

const OPTIONS: { value: PayType; label: string }[] = [
  { value: 'insurance', label: 'I want to use my insurance' },
  { value: 'cash', label: 'I plan to pay out of pocket (cash)' },
];

const POLICYHOLDER_OPTIONS: { value: SubscriberRelationship; label: string }[] = [
  { value: 'self', label: 'Myself' },
  { value: 'spouse', label: 'My spouse' },
  { value: 'child', label: 'My parent or guardian' },
  { value: 'other', label: 'Someone else' },
];

interface StepPaymentProps {
  answers: IntakeAnswers;
  dispatch: React.Dispatch<IntakeAction>;
  billingPatientId?: string;
}

export function StepPayment({ answers, dispatch }: StepPaymentProps): JSX.Element {
  const [payerOrgs] = useSearchResources('Organization', {
    type: ORG_TYPE_PAYER,
    _count: '200',
    _sort: 'name',
  });

  const payerData = (payerOrgs ?? [])
    .filter((o) => o.id && o.name)
    .map((o) => ({ value: o.id as string, label: o.name as string }));

  const isDependent = answers.subscriberRelationship !== 'self';

  return (
    <Stack gap="sm">
      <Radio.Group
        value={answers.payType ?? ''}
        onChange={(value) => dispatch({ type: 'SET_PAY_TYPE', payload: value as PayType })}
      >
        <Stack gap="sm">
          {OPTIONS.map((opt) => (
            <Radio
              key={opt.value}
              value={opt.value}
              disabled={isPayTypeIntakeDisabled(opt.value)}
              label={
                <Text span fw={500} size="sm">
                  {opt.label}
                </Text>
              }
            />
          ))}
        </Stack>
      </Radio.Group>

      <Collapse in={answers.payType === 'insurance'}>
        <Stack gap="md" mt="sm">
          <Text size="sm" c="dimmed">
            Your insurance details help us verify your coverage and match you with an in-network
            provider. You can find this information on your insurance card.
          </Text>

          <Select
            label="Insurance provider"
            placeholder="Select your insurance company"
            data={payerData}
            value={answers.insurancePayerOrgId}
            onChange={(value) =>
              dispatch({
                type: 'SET_INSURANCE_PAYER',
                payload: {
                  orgId: value,
                  name: value ? payerData.find((p) => p.value === value)?.label ?? null : null,
                },
              })
            }
            searchable
            nothingFoundMessage="No matching insurance providers"
          />
          <TextInput
            label="Member ID"
            placeholder="e.g. XYZ123456789"
            value={answers.memberId}
            onChange={(e) => dispatch({ type: 'SET_MEMBER_ID', payload: e.currentTarget.value })}
          />
          <TextInput
            label="Group ID"
            placeholder="e.g. GRP001234"
            description="Optional"
            value={answers.groupId}
            onChange={(e) => dispatch({ type: 'SET_GROUP_ID', payload: e.currentTarget.value })}
          />

          <Select
            label="Who is the primary policyholder?"
            data={POLICYHOLDER_OPTIONS}
            value={answers.subscriberRelationship}
            allowDeselect={false}
            onChange={(value) =>
              dispatch({
                type: 'SET_SUBSCRIBER_RELATIONSHIP',
                payload: (value as SubscriberRelationship | null) ?? 'self',
              })
            }
          />

          <Collapse in={isDependent}>
            <Stack gap="md">
              <Text size="sm" c="dimmed">
                Tell us about the primary policyholder so we can verify your coverage.
              </Text>
              <TextInput
                label="Policyholder first name"
                value={answers.subscriberFirstName}
                onChange={(e) =>
                  dispatch({ type: 'SET_SUBSCRIBER_FIRST_NAME', payload: e.currentTarget.value })
                }
              />
              <TextInput
                label="Policyholder last name"
                value={answers.subscriberLastName}
                onChange={(e) =>
                  dispatch({ type: 'SET_SUBSCRIBER_LAST_NAME', payload: e.currentTarget.value })
                }
              />
              <TextInput
                label="Policyholder date of birth"
                type="date"
                value={answers.subscriberDateOfBirth}
                onChange={(e) =>
                  dispatch({ type: 'SET_SUBSCRIBER_DATE_OF_BIRTH', payload: e.currentTarget.value })
                }
              />
            </Stack>
          </Collapse>

          <TextInput
            label="Coverage start date"
            type="date"
            value={answers.insuranceStartDate}
            onChange={(e) => dispatch({ type: 'SET_INSURANCE_START_DATE', payload: e.currentTarget.value })}
          />
          <TextInput
            label="Coverage end date"
            type="date"
            value={answers.insuranceEndDate}
            onChange={(e) => dispatch({ type: 'SET_INSURANCE_END_DATE', payload: e.currentTarget.value })}
          />
        </Stack>
      </Collapse>

      <Collapse in={answers.payType === 'cash'}>
        <Alert variant="light" color="blue" icon={<IconInfoCircle size={16} />} mt="sm">
          <Text size="sm">
            Payment method information will be collected as part of your intake paperwork after booking.
          </Text>
        </Alert>
      </Collapse>
    </Stack>
  );
}
